// =====================================================================
// src/modulos/proprietarios.js — o modal "Proprietários" da imobiliária (0101)
//
// UM componente, aberto de dois lugares: o botão "Proprietários" do topo da
// aba Flats (`modulos/flats.js`) e o do topo do Dashboard da imobiliária
// (`modulos/dashboard.js`). Hugo, 02/11/2026: "dashboard e aba".
//
// DE ONDE VEIO O DESENHO, do MdM (index.html):
//   · a lista: `modalGerenciarProprietarios` (10128) — título "[ic-flats]
//     Proprietários"; cada linha `display:flex;align-items:center;gap:10px;
//     padding:10px 0;border-bottom:1px solid #f3f4f6`, nome 14px 600
//     #111827, telefone e e-mail 11px #6b7280, Pix 11px #3730a3 com
//     `<code>` em #eef2ff, ou "sem dados de contato/pix" 11px #9ca3af
//     itálico; editar (btn-secondary btn-sm) e excluir (btn-danger btn-sm);
//     rodapé "Fechar" (btn-secondary flex:1) e "+ Novo proprietário"
//     (btn-primary flex:1);
//   · o formulário: `modalNovoProprietario` (10171) e `editarProprietario`
//     (10265) — form-row com label/input, os placeholders de lá, o erro na
//     caixa vermelha (#fee2e2 / #fecaca / #991b1b), rodapé Voltar/Adicionar
//     (Cancelar/Salvar na edição).
//
// ONDE DIVERGE, E POR QUÊ (esboço da 0101, aprovado):
//   · sem a foto do dono (lá obrigatória) e com CPF/CNPJ;
//   · "Excluir" pede confirmação e o banco decide: sem flat e sem acordo
//     de repasse apaga, com um dos dois DESATIVA — e a tela diz qual foi;
//   · o desativado aparece no fim da lista com a pílula "Inativo" e, no
//     lugar da lixeira, o botão "Reativar" (btn-secondary btn-sm) —
//     `reativar_proprietario` (Hugo, 02/11/2026);
//   · com `ver flats` (sem editar) a lista é só leitura: sem lápis, sem
//     lixeira, sem "+ Novo".
//
// QUEM VÊ O QUÊ é do banco: `proprietarios_da_imobiliaria()` devolve só os
// donos DELA, e as três escritas recusam o que não é dela.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, confirmar } from '../ui.js';
import { contatoDoProprietario, ordenarProprietarios, validarProprietario, argumentosDoProprietario,
         avisoDaExclusao, avisoDaReativacao, podeEditarProprietarios } from '../lib/proprietarios.js';
import { formatarDocumento } from '../lib/termo.js';

const ic = (nome, w) => { const i = icone(nome); Object.assign(i.style, { width: w + 'px', height: w + 'px', verticalAlign: '-3px', marginRight: '6px' }); return i; };
// a caixa de erro do MdM (modalNovoProprietario)
const caixaDeErro = () => el('div', { id: 'prop-erro', role: 'alert', class: 'hidden',
  style: { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '6px', padding: '8px 12px', margin: '8px 0', fontSize: '12px' } });

export async function abrirProprietarios({ auth, contexto, aoMudar = () => {} }) {
  const edita = podeEditarProprietarios(contexto);
  let lista;
  try { lista = (await auth.rpc('proprietarios_da_imobiliaria', {})) || []; }
  catch (e) { aviso(e.message, 'erro'); return; }
  const recarregar = async () => { aoMudar(); await abrirProprietarios({ auth, contexto, aoMudar }); };

  // `modalGerenciarProprietarios` (10128)
  function linha(p) {
    const c = contatoDoProprietario(p);
    const inativo = p.ativo === false;
    return el('div', { dataset: { proprietario: p.id, ativo: inativo ? '0' : '1' },
      style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f3f4f6' } },
      el('div', { style: { flex: '1', minWidth: '0' } },
        el('div', { style: { fontSize: '14px', fontWeight: '600', color: inativo ? '#9ca3af' : '#111827' } }, p.nome,
          inativo ? el('span', { class: 'badge badge-gray', style: { marginLeft: '8px' } }, 'Inativo') : null),
        c.telefone ? el('div', { style: { fontSize: '11px', color: '#6b7280' } }, c.telefone) : null,
        c.email ? el('div', { style: { fontSize: '11px', color: '#6b7280' } }, c.email) : null,
        c.pix ? el('div', { style: { fontSize: '11px', color: '#3730a3' } }, 'Pix: ',
          el('code', { style: { fontSize: '11px', background: '#eef2ff', padding: '1px 5px', borderRadius: '3px' } }, c.pix)) : null,
        c.vazio ? el('div', { style: { fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' } }, 'sem dados de contato/pix') : null),
      edita ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', title: 'Editar', 'aria-label': 'Editar ' + p.nome,
        dataset: { editarProprietario: p.id }, onclick: () => formulario(p) }, icone('ic-edit')) : null,
      edita && !inativo ? el('button', { type: 'button', class: 'btn btn-danger btn-sm', title: 'Excluir', 'aria-label': 'Excluir ' + p.nome,
        dataset: { excluirProprietario: p.id }, onclick: () => excluir(p) }, icone('ic-trash')) : null,
      edita && inativo ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', 'aria-label': 'Reativar ' + p.nome,
        dataset: { reativarProprietario: p.id }, onclick: () => reativar(p) }, 'Reativar') : null);
  }

  async function reativar(p) {
    try { await auth.rpc('reativar_proprietario', { p_id: p.id }); }
    catch (e) { aviso(e.message, 'erro'); return; }
    aviso(avisoDaReativacao(p.nome));
    await recarregar();
  }

  async function excluir(p) {
    const sim = await confirmar({ titulo: `Excluir ${p.nome}?`,
      texto: 'Sem flat e sem acordo de repasse, o cadastro é apagado. Com um dos dois, ele é desativado: some do seletor dos flats e o histórico do financeiro continua.',
      sim: 'Excluir', nao: 'Voltar', perigoso: true });
    if (!sim) { await abrirProprietarios({ auth, contexto, aoMudar }); return; }
    let r;
    try { r = await auth.rpc('excluir_proprietario', { p_id: p.id }); }
    catch (e) { aviso(e.message, 'erro'); await abrirProprietarios({ auth, contexto, aoMudar }); return; }
    aviso(avisoDaExclusao(r, p.nome));
    await recarregar();
  }

  // `modalNovoProprietario` (10171) / `editarProprietario` (10265)
  function formulario(p) {
    const novo = !p;
    const c = {};
    const campo = (id, rotulo, attrs) => el('div', { class: 'form-row' },
      el('label', { class: 'label', for: id }, rotulo),
      (c[id] = el('input', { class: 'input', id, ...attrs })));
    const erro = caixaDeErro();
    const mostrarErro = m => { erro.textContent = m || ''; erro.classList.toggle('hidden', !m); };
    const salvar = el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-salvar-proprietario', style: { flex: '1' },
      onclick: async () => {
        const f = { nome: c['prop-nome'].value, documento: c['prop-doc'].value, telefone: c['prop-tel'].value, email: c['prop-email'].value, pix: c['prop-pix'].value };
        const erros = validarProprietario(f);
        if (erros.length) { mostrarErro(erros.join(' · ')); return; }
        salvar.disabled = true;
        try { await auth.rpc('salvar_proprietario', argumentosDoProprietario(novo ? null : p.id, f)); }
        catch (e) { mostrarErro(e.message); salvar.disabled = false; return; }
        aviso(novo ? `${f.nome.trim()} adicionado.` : 'Proprietário salvo.');
        await recarregar();
      } }, novo ? 'Adicionar' : 'Salvar');
    abrirModal(el('div', { id: 'form-proprietario' },
      el('h2', { class: 'modal-title' }, novo ? 'Novo Proprietário' : 'Editar Proprietário'),
      erro,
      campo('prop-nome', 'Nome *', { placeholder: 'Ex: João Silva', value: novo ? '' : p.nome, autocomplete: 'off' }),
      campo('prop-doc', 'CPF/CNPJ', { placeholder: '000.000.000-00', value: novo || !p.documento ? '' : formatarDocumento(p.documento), inputmode: 'numeric' }),
      campo('prop-tel', 'Telefone', { placeholder: '(31) 99999-9999', type: 'tel', value: (p && p.telefone) || '' }),
      campo('prop-email', 'Email', { placeholder: 'proprietario@email.com', type: 'email', value: (p && p.email) || '' }),
      campo('prop-pix', 'Pix', { placeholder: 'CPF, CNPJ, e-mail ou chave aleatória', value: (p && p.pix) || '' }),
      el('div', { style: { display: 'flex', gap: '8px', marginTop: '6px' } },
        el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-voltar-proprietario', style: { flex: '1' },
          onclick: () => abrirProprietarios({ auth, contexto, aoMudar }) }, novo ? 'Voltar' : 'Cancelar'),
        salvar)), { largura: '480px' });
    setTimeout(() => c['prop-nome'].focus(), 0);
  }

  const ordenada = ordenarProprietarios(lista);
  abrirModal(el('div', { id: 'modal-proprietarios' },
    el('h2', { class: 'modal-title' }, ic('ic-flats', 18), 'Proprietários'),
    el('div', { id: 'lista-proprietarios' }, ordenada.length ? ordenada.map(linha)
      : el('p', { style: { fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '16px 0' } }, 'Nenhum proprietário cadastrado.')),
    el('div', { style: { display: 'flex', gap: '8px', marginTop: '16px' } },
      el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-fechar-proprietarios', style: { flex: '1' }, onclick: () => fecharModal() }, 'Fechar'),
      edita ? el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-novo-proprietario', style: { flex: '1' }, onclick: () => formulario(null) }, '+ Novo proprietário') : null)),
    { largura: '480px' });
}
